import React, { useState } from "react"

import { useHistory } from "react-router-dom"
import { gql, useQuery } from "urql"

import { Color, Size } from "~tailwindui/types/enums"

import { path } from "~Tutor/TutorRoutes"
import { WithLoadingIndicator } from "~tailwindui/Basics"
import Table from "~tailwindui/Table/Table"
import { buttonClassNames } from "~tailwindui/helpers/classNameHelpers"

import classNames from "src/classNames"

import LocalTime from "components/LocalTime"
import TimeProtectedLink from "components/TimeProtectedLink"
import TimeProtectedMiroLink from "components/TimeProtectedMiroLink"
import TutorCloudroomButton from "components/Tutor/components/Sessions/TutorCloudroomButton"

export type ScheduledSessionsProps = {
  studentId: string
}

const ScheduledSessions: React.FC<ScheduledSessionsProps> = ({ studentId }) => {
  const history = useHistory()
  const [page, setPage] = useState(1)
  const [result] = useQuery({
    query: scheduledSessionsQuery,
    variables: { studentId: studentId, page: page },
  })

  return (
    <>
      <div className="row">
        <WithLoadingIndicator result={result}>
          {({ data: { viewer } }) => (
            <Table
              title="Future Sessions"
              data={viewer.sessions}
              changePage={setPage}
              rowCallback={({ id }) => {
                history.push(path("sessionDetails", { id }))
              }}
              cols={[
                {
                  header: "Subject",
                  value: session => (
                    <span key={session.id} className="flex items-center">
                      {session.subject.name}
                    </span>
                  ),
                },
                {
                  header: "Date",
                  value: session => (
                    <LocalTime
                      key={session.id}
                      format="ddd MMM D"
                      timestamp={session.startsAt}
                    />
                  ),
                },
                {
                  header: "Time",
                  value: session => (
                    <span key={session.id}>
                      <LocalTime omitDate timestamp={session.startsAt} />
                      &mdash;
                      <LocalTime omitDate timestamp={session.endsAt} />
                    </span>
                  ),
                },
                {
                  header: "Actions",
                  value: session => (
                    <div className="flex flex-col gap-2 sm:flex-row">
                      {session.cloudroomActive && (
                        <TutorCloudroomButton
                          startsAt={session.startsAt}
                          id={session.id}
                        />
                      )}
                      {session.realtimeboards.length > 0 && (
                        <TimeProtectedMiroLink
                          tailwind
                          startsAt={session.startsAt}
                          email={viewer.ssoEmail}
                          boardId={session.realtimeboards[0].id}
                          boardPending={session.realtimeboardPending}
                          buttonClassName={buttonClassNames({
                            size: Size.Small,
                            color: Color.Green,
                          })}
                        />
                      )}
                      {session.googledocUrl && (
                        <TimeProtectedLink
                          tailwind
                          text="Google Doc"
                          href={session.googledocUrl}
                          startsAt={session.startsAt}
                          className={classNames(
                            buttonClassNames({
                              color: Color.Blue,
                              size: Size.Small,
                            })
                          )}
                        />
                      )}
                    </div>
                  ),
                },
              ]}
            />
          )}
        </WithLoadingIndicator>
      </div>
    </>
  )
}

const scheduledSessionsQuery = gql`
  query ScheduledSessions($studentId: ID!, $page: Int!) {
    viewer {
      sessions(
        filters: {
          studentId: $studentId
          future: true
          statusState: scheduled
          billState: unbilled
        }
        page: $page
      ) {
        currentPage
        totalPages
        data {
          id
          startsAt
          endsAt
          cloudroomActive
          immovable
          billState
          mayCancelByTutor
          googledocUrl
          realtimeboardPending
          realtimeboards {
            id
            name
          }
          subject {
            name
          }
        }
      }
    }
  }
`

export default ScheduledSessions
